import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Settings, BarChart3, FileText, Package, Menu, X, LogOut, Shield,
  Moon, Sun, ShoppingCart, Truck, ClipboardList, ListTodo, ChevronLeft, ChevronRight,
} from 'lucide-react';

const Navigation = ({ 
  activeTab = 'contacts', 
  onTabChange = () => {}, 
  user = null, 
  onLogout = () => {},
  isMobileOpen: externalIsMobileOpen = null,
  onMobileToggle: externalOnMobileToggle = null,
  theme = 'light',
  onToggleTheme = () => {},
  isCollapsed = false,
  onToggleCollapse = () => {},
}) => {
  const [internalIsMobileOpen, setInternalIsMobileOpen] = useState(false);
  const isDark = theme === 'dark';
  
  const useExternalState = externalIsMobileOpen !== null && externalIsMobileOpen !== undefined && externalOnMobileToggle !== null;
  const isMobileOpen = useExternalState ? externalIsMobileOpen : internalIsMobileOpen;
  
  const toggleMobileMenu = () => {
    const newValue = !isMobileOpen;
    if (useExternalState && externalOnMobileToggle) {
      externalOnMobileToggle(newValue);
    } else {
      setInternalIsMobileOpen(newValue);
    }
  };
  
  const closeMobileMenu = () => {
    if (useExternalState && externalOnMobileToggle) {
      externalOnMobileToggle(false);
    } else {
      setInternalIsMobileOpen(false);
    }
  };
  
  const navItems = [
    { id: 'contacts',        label: 'Contacto',         icon: Users },
    { id: 'products',        label: 'Productos',        icon: Package },
    { id: 'proposals',       label: 'Cotizaciones',     icon: FileText },
    { id: 'customer-orders', label: 'Pedido',           icon: ClipboardList },
    { id: 'full-orders',     label: 'Orden Completa',   icon: ShoppingCart },
    { id: 'purchase-orders', label: 'PO Individual',    icon: Truck },
    { id: 'follow-up',       label: 'Follow UP',        icon: ListTodo },
    { id: 'reports',         label: 'Reportes',         icon: BarChart3 },
    { id: 'audit',           label: 'Auditoría',        icon: Shield },
    { id: 'settings',        label: 'Configuraciones',  icon: Settings },
  ];

  const handleNavClick = (itemId) => {
    onTabChange(itemId);
    closeMobileMenu();
  };

  // Clases para el aside según estado
  const sidebarWidth = isCollapsed ? 'lg:w-14' : 'lg:w-64';

  return (
    <>
      {/* Botón menú móvil */}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleMobileMenu(); }}
        className={`fixed top-4 left-4 z-[100] lg:hidden p-2 rounded-lg shadow-lg transition-colors cursor-pointer ${
          isDark ? 'bg-[#202124] text-[#e8eaed] hover:bg-[#2b2b2b]' : 'bg-white hover:bg-gray-100'
        }`}
        aria-label="Toggle menu"
        aria-expanded={isMobileOpen}
      >
        {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Backdrop móvil */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeMobileMenu}
            className="fixed inset-0 bg-black/50 z-[40] lg:hidden cursor-pointer"
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`
          w-64 ${sidebarWidth} flex-shrink-0 border-r shadow-sm
          flex flex-col
          fixed left-0 top-0 h-screen z-[50] transform
          lg:relative lg:z-auto lg:translate-x-0
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isDark ? 'bg-[#202124] border-[#3c4043]' : 'bg-white border-gray-200'}
          transition-all duration-300 ease-in-out overflow-hidden
        `}
        aria-label="Menú principal"
      >
        <div className="flex flex-col h-full overflow-hidden min-h-0">

          {/* Header */}
          <div className={`flex-shrink-0 flex items-center border-b min-h-0 ${
            isDark ? 'border-[#3c4043]' : 'border-gray-200'
          } ${isCollapsed ? 'justify-center p-3' : 'justify-between px-4 py-4'}`}>
            {!isCollapsed && (
              <h2 className={`text-xl font-bold truncate ${isDark ? 'text-[#e8eaed]' : 'text-gray-900'}`}>
                Orilat
              </h2>
            )}

            {/* Botón colapsar — solo desktop */}
            <button
              type="button"
              onClick={onToggleCollapse}
              title={isCollapsed ? 'Expandir menú' : 'Colapsar menú'}
              className={`hidden lg:flex items-center justify-center rounded-lg transition-colors flex-shrink-0 ${
                isCollapsed ? 'w-8 h-8' : 'w-7 h-7 ml-auto'
              } ${
                isDark
                  ? 'text-[#bdc1c6] hover:bg-[#2b2b2b] hover:text-[#e8eaed]'
                  : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'
              }`}
            >
              {isCollapsed
                ? <ChevronRight className="w-4 h-4" />
                : <ChevronLeft className="w-4 h-4" />
              }
            </button>
          </div>

          {/* Nav items */}
          <nav className={`flex-1 overflow-y-auto overflow-x-hidden scrollbar-enterprise ${
            isCollapsed ? 'p-2 space-y-1' : 'p-3 space-y-1'
          }`}>
            {navItems.map((item, index) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              
              return (
                <div key={item.id} className="relative group">
                  <motion.button
                    onClick={() => handleNavClick(item.id)}
                    className={`w-full flex items-center rounded-lg font-medium transition-all duration-200 text-left ${
                      isCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-2.5'
                    } ${
                      isActive
                        ? 'bg-blue-600 text-white shadow-sm'
                        : isDark
                          ? 'text-[#e8eaed] hover:text-white hover:bg-[#2b2b2b]'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                    whileTap={{ scale: 0.97 }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.04 }}
                    title={isCollapsed ? item.label : undefined}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {!isCollapsed && <span className="text-sm truncate">{item.label}</span>}
                  </motion.button>

                  {/* Tooltip al colapsar — solo desktop */}
                  {isCollapsed && (
                    <div className={`
                      hidden lg:block
                      absolute left-full top-1/2 -translate-y-1/2 ml-2 z-[200]
                      px-2.5 py-1.5 text-xs font-medium rounded-lg shadow-lg whitespace-nowrap
                      pointer-events-none opacity-0 group-hover:opacity-100
                      transition-opacity duration-150
                      ${isDark ? 'bg-[#3c4043] text-[#e8eaed]' : 'bg-gray-800 text-white'}
                    `}>
                      {item.label}
                      <div className={`absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent ${
                        isDark ? 'border-r-[#3c4043]' : 'border-r-gray-800'
                      }`} />
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Footer */}
          <div className={`flex-shrink-0 mt-auto border-t ${
            isDark ? 'border-[#3c4043]' : 'border-gray-200'
          } ${isCollapsed ? 'p-2 space-y-1' : 'p-3 space-y-2'}`}>

            {/* Toggle tema */}
            <div className="relative group">
              <button
                onClick={onToggleTheme}
                type="button"
                title={isCollapsed ? (isDark ? 'Modo claro' : 'Modo oscuro') : undefined}
                className={`w-full flex items-center rounded-lg font-medium transition-all duration-200 ${
                  isCollapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5 text-left'
                } ${
                  isDark
                    ? 'bg-[#2b2b2b] text-[#e8eaed] hover:bg-[#3c4043]'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {isDark ? <Sun className="w-5 h-5 flex-shrink-0" /> : <Moon className="w-5 h-5 flex-shrink-0" />}
                {!isCollapsed && <span className="text-sm">{isDark ? 'Modo claro' : 'Modo oscuro'}</span>}
              </button>
              {isCollapsed && (
                <div className={`
                  hidden lg:block
                  absolute left-full top-1/2 -translate-y-1/2 ml-2 z-[200]
                  px-2.5 py-1.5 text-xs font-medium rounded-lg shadow-lg whitespace-nowrap
                  pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150
                  ${isDark ? 'bg-[#3c4043] text-[#e8eaed]' : 'bg-gray-800 text-white'}
                `}>
                  {isDark ? 'Modo claro' : 'Modo oscuro'}
                  <div className={`absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent ${isDark ? 'border-r-[#3c4043]' : 'border-r-gray-800'}`} />
                </div>
              )}
            </div>

            {/* Cerrar sesión */}
            <div className="relative group">
              <motion.button
                onClick={onLogout}
                title={isCollapsed ? 'Cerrar Sesión' : undefined}
                className={`w-full flex items-center rounded-lg font-medium transition-all duration-200 bg-red-500 text-white hover:bg-red-600 ${
                  isCollapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5 text-left'
                }`}
                whileTap={{ scale: 0.97 }}
              >
                <LogOut className="w-5 h-5 flex-shrink-0" />
                {!isCollapsed && <span className="text-sm">Cerrar Sesión</span>}
              </motion.button>
              {isCollapsed && (
                <div className="hidden lg:block absolute left-full top-1/2 -translate-y-1/2 ml-2 z-[200] px-2.5 py-1.5 text-xs font-medium rounded-lg shadow-lg whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150 bg-gray-800 text-white">
                  Cerrar Sesión
                  <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-800" />
                </div>
              )}
            </div>

            {/* Usuario */}
            {user && !isCollapsed && (
              <div className={`px-3 py-2 text-sm text-center ${isDark ? 'text-[#bdc1c6]' : 'text-gray-600'}`}>
                <div className={`font-semibold truncate ${isDark ? 'text-[#e8eaed]' : 'text-gray-800'}`}>
                  {user?.nombre || user?.email}
                </div>
              </div>
            )}
            {user && isCollapsed && (
              <div className="relative group flex justify-center">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  isDark ? 'bg-[#3c4043] text-[#e8eaed]' : 'bg-blue-100 text-blue-700'
                }`}>
                  {(user?.nombre || user?.email || '?')[0].toUpperCase()}
                </div>
                <div className={`
                  hidden lg:block absolute left-full top-1/2 -translate-y-1/2 ml-2 z-[200]
                  px-2.5 py-1.5 text-xs font-medium rounded-lg shadow-lg whitespace-nowrap
                  pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150
                  ${isDark ? 'bg-[#3c4043] text-[#e8eaed]' : 'bg-gray-800 text-white'}
                `}>
                  {user?.nombre || user?.email}
                  <div className={`absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent ${isDark ? 'border-r-[#3c4043]' : 'border-r-gray-800'}`} />
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Navigation;
